import api from './api';

export type Grain = 'month' | 'week' | 'day' | 'hour';
export type Direction = 'out' | 'in';

export interface SourceHeader {
  id: string;
  name: string;
  sourceType: string;
  integrationId: string;
  mode: string;
  organizationId: string;
  organizationName: string;
  healthStatus: string;
  syncStatus: string | null;
  syncFrequency: string | null;
  syncEnabled: boolean;
  isActive: boolean;
  lastSyncAt: string | null;
  nextSyncAt: string | null;
  timezone: string;
}

export interface SyncRun {
  startedAt: string;
  completedAt: string | null;
  ok: boolean;
  recordsCreated: number;
  /** Push-fed runs only (an inbound_emails row) — makes the tick deep-linkable. */
  runId: string | null;
  /** Inbound lifecycle status; null for scheduled syncs, which only have `ok`. */
  status: string | null;
}

export interface Coverage {
  /** Plain `YYYY-MM-DD` in business time — format with `formatPlain`, never `new Date`. */
  earliestRecord: string | null;
  latestRecord: string | null;
  months: number;
  totalRecords: number;
  addedToday: number;
  gapDays: number;
}

export interface PageAccount {
  id: string;
  name: string | null;
  type: string | null;
  businessTimezone: string | null;
  recordsToday: number;
}

export interface PartitionScope {
  slug: string;
  label: string;
  values: Array<{ value: string; count: number }>;
  active: string | null;
}

export interface Scope {
  accounts: PageAccount[];
  selectedAccountIds: string[];
  rollupAllowed: boolean;
  notes: string[];
  partition: PartitionScope | null;
  direction: Direction | null;
}

export interface Kpi {
  label: string;
  value: number;
  unit: string | null;
  deltaPct: number | null;
  priorValue: number | null;
}

export interface SeriesBucket {
  /** Plain `YYYY-MM-DDTHH:mm:ss` in business time — format with `formatPlain`. */
  start: string;
  current: number;
  prior: number | null;
  partial: boolean;
  shortfall: string | null;
}

export interface Series {
  grain: Grain;
  currentLabel: string;
  priorLabel: string;
  priorAvailable: boolean;
  metricLabel: string;
  unit: string | null;
  chartTreatment: 'sum' | 'signed_split' | 'abs_sum';
  /** The zone bucket edges are expressed in. */
  timezone: string;
  buckets: SeriesBucket[];
}

export interface DimensionOption {
  slug: string;
  label: string;
  level: 'transactions' | 'transaction_details';
}

export interface Leaderboard {
  available: true;
  dimension: DimensionOption;
  options: DimensionOption[];
  metricLabel: string;
  unit: string | null;
  rows: Array<{ label: string; value: number; deltaPct: number | null }>;
  tail: { value: number; count: number } | null;
}

export interface LeaderboardUnavailable {
  available: false;
  dimension: DimensionOption;
  options: DimensionOption[];
  message: string;
}

export type LeaderboardResult = Leaderboard | LeaderboardUnavailable;

export interface RecentRecords {
  columns: Array<{ slug: string; label: string; dataType: string }>;
  rows: Array<{
    id: string;
    timestamp: string | null;
    values: Record<string, unknown>;
    childCount: number | null;
  }>;
}

export type DegradedState =
  | 'backfill_in_progress'
  | 'no_prior_period'
  | 'unhealthy'
  | 'paused'
  | 'zero_records';

export interface PulseCard {
  resultId: string;
  severity: string;
  headline: string;
  impact: number | null;
  impactIsEstimate: boolean;
  detectedAt: string;
  threadUrl: string | null;
  accountName: string;
}

export interface SourcePageOverview {
  source: SourceHeader;
  runs: SyncRun[];
  coverage: Coverage;
  scope: Scope;
  states: DegradedState[];
  kpis: Kpi[] | null;
  series: Series | null;
  leaderboard: LeaderboardResult | null;
  recentRecords: RecentRecords | null;
  pulse: PulseCard[];
}

export interface OverviewParams {
  accountId?: string;
  grain?: Grain;
  partition?: string;
  direction?: Direction;
}

export const sourcePageApi = {
  getOverview: async (
    sourceId: string,
    params: OverviewParams = {},
  ): Promise<SourcePageOverview> => {
    const { data } = await api.get(`/api/sources/${sourceId}/overview`, { params });
    return data.data as SourcePageOverview;
  },
};

/**
 * The partition and grain choices are remembered per source in the browser only.
 * The page is deliberately read-only — persisting them server-side would add a
 * write path and a permission to a page that has neither.
 */
const PREF_KEY = 'zamino.sourcePage.prefs';

export interface SourcePagePrefs {
  partition?: string;
  grain?: Grain;
  direction?: Direction;
  accountId?: string;
}

export function readPrefs(sourceId: string): SourcePagePrefs {
  try {
    const all = JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}');
    return all[sourceId] ?? {};
  } catch {
    return {};
  }
}

export function writePrefs(sourceId: string, prefs: SourcePagePrefs): void {
  try {
    const all = JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}');
    all[sourceId] = { ...all[sourceId], ...prefs };
    localStorage.setItem(PREF_KEY, JSON.stringify(all));
  } catch {
    // A full or disabled localStorage must never break the page.
  }
}
